import type { Anchor } from './anchor';
import { wordsOf } from './anchor';
import data from './uthmani.json';

export type SurahMeta = {
  n: number;
  name: string;
  englishName: string;
  /** False for al-Fātiḥa (where it is āya 1) and at-Tawba (which has none). */
  basmalah: boolean;
  ayahCount: number;
};

/**
 * The ONLY module that knows where the text comes from.
 *
 * The design puts the whole source behind this interface so that licensing
 * KFGQPC later replaces one implementation — with real mushaf page geometry,
 * the "page 293, third line" a teacher points at — without touching anchors,
 * the renderer's contract, or anything on the wire. Everything above this file
 * speaks in (surah, ayah, word).
 */
export interface QuranTextSource {
  surahs(): SurahMeta[];
  surah(n: number): SurahMeta | null;
  ayah(surah: number, ayah: number): string | null;
  /** The words of one ayah, split by the same rule an anchor was made with. */
  words(surah: number, ayah: number): string[];
  /** The basmalah, which is a surah heading rather than part of āya 1. */
  basmalah(): string;
  /** True when an anchor points at something that actually exists. */
  isValid(a: Anchor): boolean;
}

type RawSurah = { n: number; name: string; englishName: string; basmalah: boolean; ayahs: string[] };
const RAW = data as unknown as { basmalah: string; surahs: RawSurah[] };

const META: SurahMeta[] = RAW.surahs.map((s) => ({
  n: s.n, name: s.name, englishName: s.englishName,
  basmalah: s.basmalah, ayahCount: s.ayahs.length,
}));

const at = (n: number): RawSurah | null =>
  n >= 1 && n <= RAW.surahs.length ? RAW.surahs[n - 1] : null;

/**
 * The bundled Tanzil-derived Uthmani text, built by scripts/build-quran.mjs.
 *
 * Bundled rather than fetched: this is an app for correcting recitation, and a
 * student whose connection drops mid-āya should still see the page. The build
 * script verifies the surah and āya counts before writing, so a corrupt file
 * cannot reach here silently.
 */
export const bundledQuran: QuranTextSource = {
  surahs: () => META,
  surah: (n) => META[n - 1] ?? null,
  ayah: (surah, ayah) => {
    const s = at(surah);
    if (!s || ayah < 1 || ayah > s.ayahs.length) return null;
    return s.ayahs[ayah - 1];
  },
  words: (surah, ayah) => {
    const text = bundledQuran.ayah(surah, ayah);
    return text ? wordsOf(text) : [];
  },
  basmalah: () => RAW.basmalah,
  isValid: (a) => {
    const s = at(a.surah);
    if (!s || a.ayah < 1 || a.ayah > s.ayahs.length) return false;
    // word may sit one past the end: that is "finished this āya".
    return a.word >= 0 && a.word <= wordsOf(s.ayahs[a.ayah - 1]).length;
  },
};
